import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { areEqual } from 'Utils/equalityChecks';
import { Carousel } from 'bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { carouselPhotoDeletedSelector } from 'Containers/PhotoViewCarousel/selector';
import { listCarouselPhotos } from 'Containers/RocketScan/PhotoView/Carousel/actions';
import {
  currentPageSelector,
  fetchingPhotosSelector,
  lastPageSelector,
  rocketScanPhotosSelector,
} from 'Containers/RocketScan/PhotoView/Carousel/selectors';
import { useRoomsViewFunctions } from 'Context/Project/RoomsView';
import { projectIdSelector } from 'Containers/RocketScan/selectors';
import { Carousal } from 'Components/RocketScan';
import { setCarouselPhotoDeleted } from 'Containers/PhotoViewCarousel/actions';

const CarouselContainer = () => {
  const dispatch = useDispatch();

  const carouselRef = useRef(undefined);

  const { currentPhotoId, closePhotoViewModal }: any = useRoomsViewFunctions();

  // carousel selectors
  const photos = useSelector(rocketScanPhotosSelector, areEqual);
  const currentPage = useSelector(currentPageSelector, areEqual);
  const lastPage = useSelector(lastPageSelector, areEqual);
  const fetching = useSelector(fetchingPhotosSelector, areEqual);

  // other selectors
  const projectId = useSelector(projectIdSelector, areEqual);
  const carouselPhotoDeleted = useSelector(carouselPhotoDeletedSelector, areEqual);

  const [carousel, setCarousel] = useState(null);

  useEffect(() => {
    setCarousel(new Carousel(carouselRef.current));
  }, []);

  const getPhotosFromPhotoId = useCallback(
    (photoId) => {
      const pageNumber = 1;
      dispatch(listCarouselPhotos(projectId, photoId, pageNumber));
    },
    [projectId]
  );

  const getPhotosFromPageNumber = useCallback(
    (pageNumber = 1) => {
      const paginatedFromId = '';
      dispatch(listCarouselPhotos(projectId, paginatedFromId, pageNumber));
    },
    [projectId]
  );

  // used for initial fetch where we have the photo ID
  useEffect(() => {
    if (projectId && carousel && currentPhotoId) {
      getPhotosFromPhotoId(currentPhotoId);
    }
  }, [projectId, carousel, currentPhotoId]);

  // we don't have access to the next or previous photo's ID, so we get the image using pagination
  const onClickNext = useCallback(() => {
    let pageNumber = currentPage + 1;

    if (currentPage === lastPage) {
      pageNumber = 1;
    }

    getPhotosFromPageNumber(pageNumber);
  }, [projectId, lastPage, currentPage]);

  // onClick previous button we'll get the previous image by decreasing the pagination
  const onClickPrevious = useCallback(() => {
    let pageNumber = currentPage - 1;

    if (currentPage === 1) {
      pageNumber = lastPage;
    }

    getPhotosFromPageNumber(pageNumber);
  }, [projectId, currentPage, lastPage]);

  // if the user deletes a image, we'll perform the following actions
  useEffect(() => {
    if (carouselPhotoDeleted) {
      // to get the next available image
      let pageNumber = currentPage + 1;

      if (pageNumber === lastPage) {
        pageNumber = 1;
      }

      getPhotosFromPageNumber(pageNumber);

      // redirect user back if the result is empty after photos deleted
      if (photos.length === 0) {
        closePhotoViewModal();
      }
    }

    // clear the deleted condition
    return () => {
      if (carouselPhotoDeleted) {
        dispatch(setCarouselPhotoDeleted(false));
      }
    };
  }, [carouselPhotoDeleted, photos, currentPage, lastPage]);

  return (
    <Carousal
      ref={carouselRef}
      photos={photos}
      fetching={fetching}
      onClickNext={onClickNext}
      onClickPrevious={onClickPrevious}
    />
  );
};

const CarouselContainerMemo = memo(CarouselContainer, areEqual);

export { CarouselContainerMemo as CarouselContainer };
